import { QueryClient } from '@tanstack/react-query';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';

/**
 * Shared QueryClient. gcTime (60 min) is the navigation cache floor: an unused page's
 * cache survives a full hour, so returning to it paints the cached frame instantly and
 * refreshes in the background (staleTime 5 min). Matches the sessionStorage persister's
 * maxAge below — one consistent "how stale may a first paint be" answer everywhere.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 60, // 60 minutes — navigation cache floor
      retry: 1,
    },
  },
});

// SUP-02: persist the query cache across the full-page reloads that MSAL's
// interactive-redirect renewal forces (~24h after each sign-in) so the UI
// rehydrates instantly instead of re-showing every spinner. sessionStorage on
// purpose, NOT localStorage: this is client PII in a staff app — per-tab
// sessionStorage survives the same-tab auth redirect round-trip (the exact
// SUP-02 scenario) but never outlives the tab or leaks across tabs/profiles.
// Persisted data older than staleTime still refetches in the background;
// maxAge just caps how stale a snapshot we are willing to paint first.
const queryCachePersister = createSyncStoragePersister({
  storage: window.sessionStorage,
  key: 'supervision.queryCache',
});

export const queryCachePersistOptions = {
  persister: queryCachePersister,
  maxAge: 1000 * 60 * 60, // 1 hour — an auth redirect round-trip takes seconds
  buster: 'supervision-cache-v1', // bump to invalidate persisted snapshots on breaking shape changes
};
